
import {Store} from '@gongxigames/framework/core/fragments/store/Store';
import {Signal} from '@preact/signals-core';
import {getProp} from './utils';

type PropHandlerFn = (val: any, obj: any, key: string) => void;
type PropHandlerOptions = {
    forceHandleAsSignal?: boolean;
};
type PropHandler = PropHandlerOptions & {
    fn: PropHandlerFn;
};
type GetSignalPropType<T> = T extends Signal ? never : T extends undefined ? never : T;

const DEFAULT_HANDLER: PropHandler = {
    fn: (val: any, obj: any, key: string ) => obj[key] = val
};

export class SignalPropsHandler<T extends {}, OBJ extends {}>{
    readonly handlers: Map<keyof T, PropHandler> = new Map();
    readonly propsToHandleList: (keyof T)[] = [];

    constructor() {}

    propsToHandle<
        K extends keyof T
    >(props: readonly K[]): this {
        this.propsToHandleList.push(...props);

        return this;
    }

    setHandlerFor<
        K extends keyof T,
        FN extends(val: GetSignalPropType<T[K]>, obj: OBJ, key: K) => void,
    >(key: K, fn: FN, options: PropHandlerOptions = {}): this {
        const handler = options as PropHandler;

        handler.fn = fn as any;
        this.handlers.set(key, handler);

        return this;
    }

    apply(props: Partial<T>, obj: OBJ): () => void {
        const unsubscribeCallbacks: (() => void)[] = [];

        this
            .getPropKeysToHandle(props)
            .forEach(key => {
                const val = props[key];
                const off = this.handleProp(key, val, obj);

                if (off) {
                    unsubscribeCallbacks.push(off);
                }
            });

        return () => unsubscribeCallbacks.forEach(fn => fn());
    }

    private getPropKeysToHandle(props: Partial<T>) {
        return this.propsToHandleList
            .filter(key => Object.prototype.hasOwnProperty.call(props, key));
    }

    private handleProp<K extends keyof T>(key: K, val: Partial<T>[K], obj: OBJ) {
        const isSignal = Store.isSignal(val);
        const handler = this.handlers.get(key) || DEFAULT_HANDLER;

        if (isSignal || handler.forceHandleAsSignal) {
            return Store.effect(() => {
                const signalVal = getProp(val);

                handler.fn(signalVal, obj, key as string);
            });
        }

        handler.fn(val, obj, key as string);
    }
}